// @flow

import React, { useEffect } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { GSGrid, Pagination, IconButton, View } from "@components";
import { compose } from "redux";
import { formatDateTime } from "@transformers/date";
import { formatAuthoredBy } from "@transformers/names";
import type { EmployeeAbsenceManagerNotes } from "@modules/shared/reducer/employee-absences/types";
import actionCreators from "@modules/shared/actionCreator";
import {
  getManagerNotes,
  getManagerNotesPagination,
  deleteNoteModalVisible,
  deleteNoteModalSelectedNote,
} from "@modules/shared/selectors/employee-absences";
import type { PaginationExtendedType } from "@types";
import type { State } from "@store/reducers";
import { ConfirmationModal } from "@components";
import { selectUserType } from "@modules/account/selectors";

type OwnProps = $Exact<{
  clientId: number,
  absenceId: number,
  employeeId: number,
  hideAction?: boolean,
}>;

type PropsFromState = $Exact<{
  managerNotes: Array<EmployeeAbsenceManagerNotes>,
  pagination: PaginationExtendedType,
  isDeleteNoteModalVisible: boolean,
  userType: string,
  selectedNote: ?EmployeeAbsenceManagerNotes,
}>;

type PropsFromDispatch = $Exact<{
  getManagerNotes: typeof actionCreators.GetManagerNotes,
  toggleAddEditManagerNotesModal: typeof actionCreators.ToggleAddEditManagerNotesModal,
  toggleDeleteManagerNoteModal: typeof actionCreators.ToggleDeleteManagerNoteModal,
  deleteManagerNote: typeof actionCreators.DeleteManagerNote,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export function AbsenceDetailsManagerNotesTable(props: Props) {
  useEffect(() => {
    props.getManagerNotes({
      clientId: props.clientId,
      employeeId: props.employeeId,
      absenceId: props.absenceId,
    });
  }, []);
  const data = props.managerNotes || [];
  function showEditNotesModal(item: EmployeeAbsenceManagerNotes) {
    return function() {
      props.toggleAddEditManagerNotesModal({
        editMode: true,
        visible: true,
        data: item,
      });
    };
  }

  function toggleDeleteNoteModal(item: EmployeeAbsenceManagerNotes | null) {
    return function() {
      props.toggleDeleteManagerNoteModal({ note: item });
    };
  }

  function deleteManagerNote() {
    if (props.selectedNote)
      props.deleteManagerNote({
        clientId: props.clientId,
        employeeId: props.employeeId,
        noteId: props.selectedNote.id,
        absenceId: props.absenceId,
      });
  }

  function isAllowedToSee() {
    const allowedUserTypes = ["ADMIN", "CLIENT_ADMIN", "SERVICE_MANAGER"];
    return allowedUserTypes.indexOf(props.userType) !== -1;
  }

  function columns() {
    let res = [
      {
        key: "dateAndTime",
        name: props.t("DATE_ADDED"),
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) =>
          formatDateTime(row.updatedAt),
        width: "min-content",
      },
      {
        key: "advisor",
        name: props.t("ADDED_BY"),
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) =>
          formatAuthoredBy(row.updatedBy),
        align: "left",
        width: "min-content",
      },
      {
        key: "noteType",
        name: props.t("TYPE"),
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) =>
          (row.noteType || {}).name || "",
        align: "left",
        width: "min-content",
      },
      {
        key: "notes",
        name: props.t("NOTES"),
        align: "left",
        cellClass: "p-0",
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) => (
          <textarea
            rows="4"
            key={`${row?.id || new Date().getTime()}-${JSON.stringify(
              row?.updatedAt || new Date().getTime(),
            )}`}
            disabled
            maxLength="600"
            className="no-resize background-color-white borders-solid-color-white fs-14 line-height-20"
            defaultValue={row.notes}
          />
        ),
      },
      {
        key: "editManagerNote",
        name: props.t("EDIT"),
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) => (
          <IconButton
            icon={
              <span
                className={`material-icons pointer text-color-${
                  !row.isEditableByLoggedInUser ? "disabled" : "black"
                }`}
              >
                edit
              </span>
            }
            disabled={!row.isEditableByLoggedInUser}
            onClick={showEditNotesModal(row)}
          />
        ),
        width: "min-content",
      },
    ];

    if (isAllowedToSee()) {
      res.push({
        key: "toggleDeleteNoteModal",
        name: props.t("DELETE"),
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) => (
          <span
            className="material-icons pointer font-size-26"
            onClick={toggleDeleteNoteModal(row)}
          >
            delete
          </span>
        ),
        width: "min-content",
      });
    }

    return res;
  }

  function onPageChange(page: number) {
    props.getManagerNotes({
      clientId: props.clientId,
      employeeId: props.employeeId,
      absenceId: props.absenceId,
      page,
    });
  }

  return (
    <View>
      <GSGrid
        rowHeight={r => 100}
        rows={data}
        columns={columns()}
        noResultsText={props.t("NO_NOTES_MESSAGE")}
      />
      {props.isDeleteNoteModalVisible && (
        <ConfirmationModal
          headerText={props.t("DELETE_NOTE")}
          bodyContent={props.t("CONFIRM_NOTE_DELETION")}
          confirmBtnLabel={props.t("DELETE")}
          confirmBtnIcon="delete_forever"
          onCancel={toggleDeleteNoteModal(null)}
          onConfirm={deleteManagerNote}
        />
      )}
      <Pagination
        className="my-3 d-flex flex-fill justify-content-center"
        currentPage={(props.pagination || {}).pageNumber}
        totalPages={(props.pagination || {}).totalPages}
        onChange={onPageChange}
      />
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  managerNotes: getManagerNotes(state),
  pagination: getManagerNotesPagination(state),
  isDeleteNoteModalVisible: deleteNoteModalVisible(state),
  selectedNote: deleteNoteModalSelectedNote(state),
  userType: selectUserType(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getManagerNotes: actionCreators.GetManagerNotes,
  toggleAddEditManagerNotesModal: actionCreators.ToggleAddEditManagerNotesModal,
  toggleDeleteManagerNoteModal: actionCreators.ToggleDeleteManagerNoteModal,
  deleteManagerNote: actionCreators.DeleteManagerNote,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
)(AbsenceDetailsManagerNotesTable);
